/**
 * @pdmg/reporting-schema — the report data model.
 *
 * This is the ONLY surface binding expressions may address. It is a deliberate,
 * versioned contract rather than a passthrough of Mongo documents:
 *
 *   - the builder derives autocomplete and static binding validation from it;
 *   - the metric engine populates it, so every figure has one canonical path;
 *   - changing it is a breaking change with a migration, exactly like the template
 *     schema, which is what stops templates silently rotting (scope R5).
 *
 * Field naming is camelCase throughout. The legacy Y/N string booleans and the
 * snake_case of the Sails models stop at the engine boundary.
 */

import { z } from 'zod';

export const DATA_MODEL_VERSION = 1 as const;

const Int = z.number().int().nonnegative();
const Pct = z.number();

/**
 * A metric that may legitimately be unknown — pre-flight has no delivery yet.
 * Blocks render these per their manifest's `unavailableRendering` ("TBD" by default),
 * which is what makes one template serve all three lifecycle stages (scope D5/D26).
 */
const Unknowable = <T extends z.ZodTypeAny>(inner: T) => inner.nullable();

// ---------------------------------------------------------------------------

export const Creative = z.object({
  id: z.string(),
  name: z.string().optional(),
  widthPx: Int.optional(),
  heightPx: Int.optional(),
  /** Rendered as "728 x 90". Precomputed so templates don't do string maths. */
  dimensions: z.string().optional(),
  destinationUrl: z.string().optional(),
  clicks: Unknowable(Int),
  assetId: z.string().optional(),
  isRepresentative: z.boolean().default(false),
  /**
   * Current status, for display only. NEVER a selection filter — an ad that delivered
   * in the window belongs in the totals whatever its status is now (scope F16/D33).
   */
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED', 'UNKNOWN']).default('UNKNOWN'),
  /** True when this ad delivered impressions or clicks inside the reporting period. */
  deliveredInWindow: z.boolean().default(true),
});

export const AdGroup = z.object({
  id: z.string(),
  name: z.string(),
  channel: z.enum(['DISPLAY', 'EMAIL', 'AUDIO', 'VIDEO', 'DOOH']),
  creatives: z.array(Creative),
});

export const NamedCount = z.object({ name: z.string(), clicks: Int });

/** Shared shape for the 4-up KPI row and the delivery strip. */
export const DeliveryStats = z.object({
  impressions: Unknowable(Int),
  retargetImpressions: Unknowable(Int),
  clicks: Unknowable(Int),
  retargetClicks: Unknowable(Int),
  mobileClicks: Unknowable(Int),
  desktopClicks: Unknowable(Int),
});

export const ContractStats = z.object({
  targetImpressions: Int,
  targetClicks: Int.optional(),
});

/**
 * What CTR was divided by.
 *
 * There are three denominators across the channels and they are different by design:
 * display/native/video divide by CONTRACTED impressions (PAP-371), audio by streams,
 * email by recipients. Carrying the basis on the data model lets a template render an
 * accurate footnote instead of hardcoding one — the Display template's footnote is a
 * literal prop string today, and that string is simply wrong on an email report.
 */
export const CtrBasis = z.enum(['contracted-impressions', 'streams', 'recipients']);
export type CtrBasis = z.infer<typeof CtrBasis>;

/** Everything the metric engine derives. Never authored, never overridden directly. */
export const DerivedStats = z.object({
  ctr: Unknowable(Pct),
  /** Which denominator `ctr` used. Absent only on legacy documents predating this. */
  ctrBasis: CtrBasis.optional(),
  overDeliveredImpressions: Unknowable(Int),
  overDeliveryPct: Unknowable(Pct),
});

/**
 * A companion banner — the display unit shown alongside an audio ad.
 *
 * Its clicks are retargeted clicks, and they are part of the campaign's total. See
 * the invariant note on AudioStats.
 */
export const CompanionBanner = z.object({
  name: z.string(),
  dimensions: z.string().optional(),
  clicks: Int,
  assetId: z.string().optional(),
  /** The companion banner's landing URL — shown on the audio Campaign Details creative showcase. */
  destinationUrl: z.string().optional(),
});

export const AudioStats = z.object({
  streams: Unknowable(Int),
  complimentaryDisplayImpressions: Unknowable(Int),
  adsPlayed: Unknowable(Int),
  completeListens: Unknowable(Int),
  /** ceil(completeListens * 0.75) / adsPlayed * 100 — see metric-formulas memory. */
  ltr: Unknowable(Pct),
  reach: Unknowable(Pct),

  /*
   * Audio total clicks decompose as:
   *
   *   clicks = audioCreativeClicks + companionClicks
   *   companionClicks = Σ companions[].clicks
   *
   * Verified against the mockup: 6,807 + 1,382 = 8,189, and the four companion
   * banners (305 + 274 + 413 + 390) sum to exactly the Retargeted Clicks tile.
   * These two fields exist so that decomposition is representable at all — without
   * them the audio invariant (ticket A-3) cannot be written down, let alone enforced.
   */
  audioCreativeClicks: Unknowable(Int),
  /** Σ companions[].clicks. Equals the "Retargeted Clicks" KPI tile. */
  companionClicks: Unknowable(Int),
  companions: z.array(CompanionBanner).default([]),

  /** The AUDIO creative's own dimensions / landing URL — the identity block on the Streaming Audio Summary page (its clicks are `audioCreativeClicks`). */
  creativeDimensions: z.string().optional(),
  creativeDestinationUrl: z.string().optional(),

  topStations: z.array(z.object({ name: z.string(), plays: Int })).default([]),
  genres: z.array(z.string()).default([]),
  language: z.string().optional(),
  ageGroups: z.array(z.string()).default([]),
  gender: z.string().optional(),
});

/** One impression-weighted slice for a CTV breakdown (app, publisher, device, network, state). */
export const CtvSlice = z.object({ name: z.string(), impressions: z.number().nonnegative() });
/** One city slice for the CTV geographic table. */
export const CtvCity = z.object({
  city: z.string(), state: z.string(), impressions: z.number().nonnegative(),
});
/** One quartile step in the VAST completion funnel. */
export const CtvFunnelStep = z.object({
  /** `start` | `firstQuartile` | `midpoint` | `thirdQuartile` | `complete`. */
  eventType: z.string(),
  /** Client-friendly axis label ("Started", "25%", "50%", "75%", "Completed"). */
  label: z.string(),
  eventCount: Int,
});

/**
 * CTV / OTT ("connected TV") delivery — the fifth-and-a-half channel.
 *
 * A view-based buy: the headline is the VAST-rendered impression and the video completion rate
 * (VCR), NOT clicks (CTV creatives carry no click, `target_clicks` is 0). Sourced from our own
 * `video_delivery` store (`aggregateVideoFromStore`), which consolidates `video_events` (funnel,
 * rendered impressions, geo, reach) and `impressions_v2` (apps, device, network, spend).
 */
export const CtvStats = z.object({
  /** VAST-rendered impressions — the CANONICAL, billable count and the headline figure. */
  renderedImpressions: Unknowable(Int),
  /** Won-impression records (`impressions_v2`), CTV device types only. Beside rendered for the gap. */
  servedImpressions: Unknowable(Int),
  /** Won impressions excluded as non-CTV (Phone / Tablet). Drives the disclosure line. */
  excludedNonCtv: Int.default(0),
  starts: Unknowable(Int),
  completes: Unknowable(Int),
  creativeViews: Unknowable(Int),
  /** completes / renderedImpressions * 100. Null when nothing rendered. */
  vcr: Unknowable(Pct),
  /** Distinct-IP reach (exact for a single-day flight; an upper bound across days). */
  reach: Unknowable(Int),
  /** Reach as a percentage of impressions — unique reach / impressions * 100 (= 100 / frequency). */
  reachPct: Unknowable(Pct),
  /** Average frequency — impressions / unique reach. Null when reach is unknown or zero. */
  averageFrequency: Unknowable(Pct),
  /** The quartile funnel in sequence order, for the completion chart. */
  funnel: z.array(CtvFunnelStep).default([]),
  /** Apps / channels the ad ran on ("Samsung TV Plus", "Atmosphere TV"…). */
  apps: z.array(CtvSlice).default([]),
  /** Apps crossed with device type and platform — the "apps by device" table. */
  appDevices: z.array(z.object({
    app: z.string(), deviceType: z.string(), platform: z.string(), impressions: z.number().nonnegative(),
  })).default([]),
  publishers: z.array(CtvSlice).default([]),
  /** Device environment ("Connected TV", "Set Top Box"…). */
  deviceTypes: z.array(CtvSlice).default([]),
  /** SSP / RTB network. */
  networks: z.array(CtvSlice).default([]),
  geoStates: z.array(CtvSlice).default([]),
  /** All states with their raw USPS code + impressions, for the geographic heatmap (choropleth). */
  geoStateMap: z.array(z.object({ state: z.string(), impressions: Int })).default([]),
  /** A one-line summary of the campaign's geo targeting ("National — United States", "State: CA, TX"…). */
  targetingSummary: z.string().optional(),
  /** Won impressions per calendar day across the flight (start→end), for the daily line chart. */
  impressionsByDay: z.array(z.object({ date: z.string(), label: z.string(), impressions: Int })).default([]),
  /** The scale of `impressionsByDay`: one point per day, or per week / month on a long flight. */
  impressionsSeriesUnit: z.enum(['day', 'week', 'month']).optional(),
  geoCities: z.array(CtvCity).default([]),
  /**
   * The editable "Top Cities" table: scoped to the campaign's targeting, top 10 + a reconciling "Other"
   * row so the rows sum to the headline impression. Editable, and the cascade rolls a city edit up to
   * `overall.delivery.impressions` / `ctv.starts` (see CTV_INVARIANTS). Kept as the fallback when the
   * campaign has no defined geo targets (a national buy); otherwise `geoTargets` is shown instead.
   */
  topCities: z.array(CtvSlice).default([]),
  /**
   * "Impressions by Target": delivery attributed to the campaign's DEFINED geo targets (from the Agency
   * Portal — counties / cities / DMAs / ZIPs / states), summing to the headline impression. Replaces the
   * Top Cities table on a targeted buy. `id` is stable so a reviewer can remove a target and have the
   * attribution redistribute. Empty on an untargeted (national) buy.
   */
  geoTargets: z.array(z.object({ id: z.string(), name: z.string(), impressions: Int })).default([]),
  /**
   * Reviewer-set target impression total. When present and positive, the materialiser scales the whole
   * report to it (headline, starts, top cities, apps, funnel, daily, reach — see `scaleVideoToDesired`).
   * Absent on a generated report; set through the "Desired Impressions" control in the review drawer.
   */
  desiredImpressions: z.number().int().nonnegative().optional(),
  /**
   * Reviewer removals, as JSON-array strings of keys — app/channel names and geo-target ids. Set by the
   * builder's remove control; `applyCtvRemovals` drops those rows and redistributes their impressions
   * proportionally across the survivors on materialise. Absent on a generated report.
   */
  removedApps: z.string().optional(),
  removedTargets: z.string().optional(),
  /**
   * Rendered impressions by day-of-week (row 0 = Sunday … 6 = Saturday) × hour (col 0-23), in the
   * campaign's US Eastern wall-clock — a 7×24 matrix driving the day/time heatmap. Empty until
   * consolidated.
   */
  viewsByDayHour: z.array(z.array(Int)).default([]),
  /** The video creative's identity, for the creative card. */
  creativeTitle: z.string().optional(),
  creativeDurationSeconds: z.number().nonnegative().optional(),
  creativeDestinationUrl: z.string().optional(),
  /** A rendered frame / thumbnail of the video creative (data: URI), when captured. */
  creativeAssetId: z.string().optional(),
});

/**
 * One tracked link within a drop.
 *
 * `clickShare` is legacy's `l.ctr`, renamed. It is a SHARE OF CAMPAIGN CLICKS, not a
 * click rate — legacy divides by the campaign total while its guard still tests the
 * link's own count, a leftover from when the real rate was commented out. Naming it
 * honestly here stops anyone reading a share as a rate.
 */
export const EmailLink = z.object({
  linkId: z.string().optional(),
  link: z.string(),
  clicks: Int,
  uniqueClicks: Unknowable(Int),
  clickShare: Unknowable(Pct),
});

export const CityClicks = z.object({ city: z.string(), clicks: Int });
export const DayClicks = z.object({ date: z.string(), clicks: Int });

/**
 * One deployment ("drop"). Email campaigns deploy the same creative several times, and
 * every figure on an email report is either per-drop or a sum across drops.
 */
export const EmailDrop = z.object({
  /** 1-based. Drives the "D#1" label and the ordering of the overview table. */
  dropNo: Int,
  label: z.string(),          // "D#1"
  title: z.string().optional(),
  deployDate: z.string().optional(),
  /** The deployment IO (e.g. "API15198PVA"). */
  io: z.string().optional(),
  /** The creative screenshot for this deployment — screenshot.fraudfree.net/screenshots/{io}.png. */
  creativeUrl: z.string().optional(),

  /**
   * Recipients sent — legacy `count` / `total_records`. This is email's impression
   * equivalent AND its CTR denominator; see `ctrBasis` on DerivedStats.
   */
  recipients: Unknowable(Int),
  opens: Unknowable(Int),
  uniqueOpens: Unknowable(Int),
  clicks: Unknowable(Int),
  uniqueClicks: Unknowable(Int),
  bounces: Unknowable(Int),
  mobileClicks: Unknowable(Int),
  optOuts: Unknowable(Int),
  /** True when the opt-out figure was estimated rather than measured (D44). */
  optOutsEstimated: z.boolean().default(false),

  ctr: Unknowable(Pct),
  uniqueCtr: Unknowable(Pct),
  openRate: Unknowable(Pct),

  links: z.array(EmailLink).default([]),
  citiesByClicks: z.array(CityClicks).default([]),
});

export const EmailStats = z.object({
  subject: z.string().optional(),
  fromName: z.string().optional(),
  fromEmail: z.string().optional(),
  drops: z.array(EmailDrop).default([]),

  /**
   * Campaign totals. Summed from drops — the summary RATES below are computed from
   * these sums and never averaged from per-drop rates, because averaging rates weights
   * a 100-recipient drop equally with a 100,000-recipient one.
   */
  recipients: Unknowable(Int),
  clicks: Unknowable(Int),
  uniqueClicks: Unknowable(Int),
  opens: Unknowable(Int),
  uniqueOpens: Unknowable(Int),
  bounces: Unknowable(Int),
  mobileClicks: Unknowable(Int),
  nonMobileClicks: Unknowable(Int),
  optOuts: Unknowable(Int),
  /** How many drops had their opt-outs estimated, for the provenance disclosure. */
  optOutsEstimatedCount: Int.default(0),

  ctr: Unknowable(Pct),
  uniqueCtr: Unknowable(Pct),
  openRate: Unknowable(Pct),
  uniqueOpenRate: Unknowable(Pct),
  bounceRate: Unknowable(Pct),
  /** Click-to-open. The one email rate whose denominator is OPENS, not recipients. */
  htmlCtr: Unknowable(Pct),

  /** Links rolled up across every drop, ranked by clicks. */
  aggregatedLinks: z.array(EmailLink).default([]),
  /** Campaign-wide clicks by day — the line chart on the overview page. */
  clicksByDay: z.array(DayClicks).default([]),
  citiesByClicks: z.array(CityClicks).default([]),
});

export const DoohVenue = z.object({
  screenId: z.string(),
  venueType: z.string(),
  venueName: z.string(),
  addressLine: z.string(),
  cityStateZip: z.string(),
  /**
   * Projected audience per play, which is what the top-100 cap ranks by (D42).
   *
   * Added because the decision says the list is "capped at the top 100, ranked by
   * estimated audience" while the model had no audience to rank by — so a reader could
   * see 100 of 2,847 venues and had no way to tell why those 100. A cap whose basis is
   * invisible is indistinguishable from an arbitrary truncation.
   */
  estimatedAudience: z.number().nonnegative().optional(),
});

/** Rolled-up counts for placements beyond the cap. */
export const VenueTypeCount = z.object({ venueType: z.string(), screens: Int });

/**
 * Measured delivery at one venue, post-flight.
 *
 * Deliberately NOT capped, unlike `potentialVenues`. D42 caps the pre-flight list at
 * 100 because it is a *projection* — an uncapped geo-targeted list against a 500,000
 * screen inventory (F20) runs to ~70 pages of screens that may never play anything,
 * which is noise rather than reporting.
 *
 * Post-flight is the opposite case: these are venues that actually delivered, and the
 * client paid for every one. Capping measured delivery would understate the campaign
 * and make the report's totals disagree with its own table. So this list is bounded by
 * reality rather than by a constant, which is exactly why it is the right driver for
 * pagination (B-4) — it is the one table in the catalog that can legitimately reach
 * thousands of rows.
 */
export const DoohVenueDelivery = z.object({
  screenId: z.string(),
  venueType: z.string(),
  venueName: z.string(),
  cityStateZip: z.string(),
  plays: Int,
  /** Σ impression_multiplier — audience, not plays (D38). Fractional by nature. */
  impressions: z.number().nonnegative(),
  /** True when `screen_id` had no match in `dooh_screens` at generation time (D39). */
  unmatchedScreen: z.boolean().default(false),
});

export const DoohStats = z.object({
  /**
   * Pre-flight: possible placements, **capped at the top 100** (scope D42).
   * Screen inventory is growing to 500,000, so an uncapped geo-targeted list runs to
   * ~70 pages. Capping here rather than only at render time also keeps the stored
   * report document small (R5) — embedding thousands of rows in every report would
   * bloat the collection for no reader benefit.
   */
  potentialVenues: z.array(DoohVenue).max(100).default([]),
  /** Ordering used to choose the top 100. Recorded so the cap is auditable (R9). */
  potentialVenuesRankedBy: z.enum(['audience', 'proximity', 'venueType']).default('audience'),
  /** Every placement, including those beyond the cap, rolled up by venue type. */
  venueSummary: z.array(VenueTypeCount).default([]),
  /** Total matching placements before capping. Drives the "showing 100 of N" line. */
  totalPotentialVenues: Int.default(0),
  screenCount: Int.default(0),
  /**
   * Null pre-flight. Audience impressions, `Σ impression_multiplier` (D38) — kept
   * unrounded here because real multipliers are fractional and rounding per row
   * accumulates error across thousands of them.
   */
  actualImpressions: z.number().nonnegative().nullable(),
  /**
   * Plays: `count(*)` of beacon rows. Distinct from impressions, never conflated.
   *
   * Defaulted rather than merely nullable. A field added to the data model must not
   * invalidate documents written before it existed — making this required-but-nullable
   * broke the pre-existing DOOH placement-cap assertion, which is a small preview of
   * what it would do to stored reports.
   */
  actualPlays: Int.nullable().default(null),
  /** Post-flight per-venue delivery. Uncapped — see DoohVenueDelivery. */
  venueDelivery: z.array(DoohVenueDelivery).default([]),
  /**
   * Rows whose `screen_id` matched no screen. Reported rather than bucketed into
   * "Other" (D39/R9), so an inventory sync gap is visible instead of disguised.
   */
  unmatchedScreens: Int.default(0),
  /** Beacon rows whose multiplier was non-finite and had to be coerced to 1. */
  coercedMultipliers: Int.default(0),
  mapAssetId: z.string().optional(),
  /**
   * Post-flight: impressions per calendar day across the flight (start → the earlier of the flight end
   * and the generation day), for the daily line. Weighted (Σ multiplier per day) when screen-play
   * beacons exist; the recorded delivery otherwise. Empty pre-flight.
   */
  impressionsByDay: z.array(z.object({ date: z.string(), label: z.string(), impressions: z.number().nonnegative() })).default([]),
  /** The scale of `impressionsByDay`: one point per day, or per week / month on a long flight. */
  impressionsSeriesUnit: z.enum(['day', 'week', 'month']).optional(),
  /**
   * Post-flight: true when the figures are audience-weighted screen plays (D38). Absent pre-flight.
   */
  weighted: z.boolean().optional(),
  /** Post-flight: weighted impressions by US-Eastern day-of-week (0 = Sun) × hour. 7×24, or empty. */
  viewsByDayHour: z.array(z.array(z.number().nonnegative())).default([]),
  /**
   * Post-flight: delivery per screen LOCATION from the DOOH SSP's plays (Vistar). The SSP's screens are
   * its own network and rarely match `dooh_screens`, so a location is its coordinates + network, placed
   * (city/state/ZIP) from the nearest ZIP centroid. Uncapped — measured delivery, like `venueDelivery`.
   */
  locationDelivery: z.array(z.object({
    lat: z.number(), lon: z.number(),
    network: z.string(),
    cityStateZip: z.string(),
    plays: Int,
    impressions: z.number().nonnegative(),
  })).default([]),
  /** Post-flight: delivery per screen network (GSTV, Circle K…), with its count of screen locations. */
  networkDelivery: z.array(z.object({
    name: z.string(), screens: Int, plays: Int, impressions: z.number().nonnegative(),
  })).default([]),
  /** Impressions the DOOH ads won on NON-screen (mobile / web) supply — excluded from every figure. */
  nonScreenImpressions: Int.default(0),
});

export const Targeting = z.object({
  zips: z.array(z.string()).default([]),
  radiusMiles: z.number().optional(),
  mapAssetId: z.string().optional(),
  /**
   * A rendered map image (Mapbox Streets static image, red overlays baked in) fetched at GENERATION
   * time and stored as a `data:` URI, so the render inlines it with no network at print (R4) and the
   * report stays byte-reproducible. Present only when a Mapbox token was configured and the fetch
   * succeeded; the map block prefers it, and falls back to the offline SVG when it is absent.
   */
  mapImage: z.string().optional(),
  /**
   * Which geography the campaign targets, for the offline SVG map. Absent on legacy reports (they
   * carry only ZIPs + a baked map image); the map block treats an absent mode as "no open-source map"
   * and falls back to the image/ZIP band, so old reports are unaffected. The entity lists below carry
   * the targeted values for the chosen mode — only `zips` is populated by the generate worker today;
   * the rest activate when their targeting data is wired from the source system.
   */
  geoMode: z.enum(['country', 'state', 'zip', 'city', 'county', 'dma', 'geofence']).optional(),
  /** USPS abbreviations (e.g. "MA"), FIPS, or full state names. */
  states: z.array(z.string()).optional(),
  /** 5-digit county FIPS codes — drives the county boundary on the map. */
  counties: z.array(z.string()).optional(),
  /** Display names for the counties ("Baltimore County, MD"), for the map caption (counties holds FIPS). */
  countyLabels: z.array(z.string()).optional(),
  /** "City, ST" — resolved to a projected centroid at render time. */
  cities: z.array(z.string()).optional(),
  /** DMA code or name — drawn as the union of its member counties. */
  dmas: z.array(z.string()).optional(),
  /** Drawn geofence polygons — each an array of [lng, lat] vertices (the legacy GEOFENCE geo_type). */
  geofences: z.array(z.array(z.tuple([z.number(), z.number()]))).optional(),
  audience: z.object({
    ageOverlay: z.string().optional(),
    gender: z.string().optional(),
    hhiThreshold: z.string().optional(),
    notes: z.array(z.string()).default([]),
  }).default({ notes: [] }),
  topSites: z.array(z.string()).default([]),
});

export const ChannelSummary = z.object({
  channel: z.enum(['DISPLAY', 'EMAIL', 'AUDIO', 'VIDEO', 'DOOH']),
  delivery: DeliveryStats,
  contract: ContractStats,
  derived: DerivedStats,
  devices: z.array(NamedCount).default([]),
  browsers: z.array(NamedCount).default([]),
});

// ---------------------------------------------------------------------------

/**
 * How the underlying records were selected. Recorded on every report because
 * "which ads were counted" is part of how a figure was derived (scope R2), and because
 * the current system's answer to that question changes over time (scope F16).
 */
export const SelectionCriteria = z.object({
  /**
   * ALWAYS 'delivery-in-window' for v2. Ads are selected by whether they delivered
   * impressions or clicks inside the reporting period — NOT by their present-day
   * status. Status is current state; a report is a historical window. Filtering
   * history by today's status is what forces the manual corrections today.
   */
  adSelection: z.enum(['delivery-in-window', 'active-only', 'all']),
  /** Statuses present among the selected ads, for disclosure. Never a filter. */
  adStatusesIncluded: z.array(z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED', 'UNKNOWN'])).default([]),
  adGroupSelection: z.enum(['delivery-in-window', 'active-only', 'all']),
  /** Ads that delivered but are excluded from the creative showcase for space. */
  creativesShownLimited: z.boolean().default(false),
  /** Count of delivering ads whose current status is not ACTIVE. */
  nonActiveDeliveringAds: Int.default(0),
});

/**
 * Report settings that only affect how the report is PRESENTED.
 *
 * ── Why these live in the report data ──
 *
 * The legacy settings drawer mixes two different kinds of switch. Some change what the
 * report *contains* — which channels to pull, how many email deployments, whether to
 * fetch every ad group — and those are inputs to generation: the legacy tool's Submit
 * button re-runs the whole build. Others change only how the gathered data is drawn.
 *
 * Only the second kind belongs here. Putting them in the report data means a template can
 * read them like any other value (`visibleWhen: 'presentation.showCitiesChart'`), which in
 * turn means changing one is an ordinary override: appended, audited, reversible, and
 * visible in the preview immediately. A settings mechanism that lived outside the data
 * would need its own storage, its own audit trail and its own undo.
 *
 * Every field is optional with a default, so documents generated before this existed
 * still validate and still render exactly as they did.
 */
export const Presentation = z.object({
  /**
   * Overrides the template's primary colour for this report only.
   *
   * White-labelled reports carry the client's brand colour, which is a per-report fact
   * and not a property of the template — one template serves every client. Cloning a
   * template per client is what would turn one document into seven hundred.
   *
   * Only `primary` is reachable. The rest of the palette is legibility and layout, and
   * letting a colour picker reach it is how a report ends up with white text on white.
   */
  themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected a #rrggbb colour').optional(),
  /**
   * The report's ACCENT colour (the header underline and the campaign ID# text) for this report only.
   *
   * The second reachable brand colour, alongside `themeColor`. White-label reports carry an agency's
   * two brand colours — a header background and an accent — so `accentColor` substitutes for
   * `color.accent` the way `themeColor` substitutes for `color.primary`, rather than cloning the
   * template per agency. Everything else in the palette stays legibility and layout. Fed at generation
   * from the agency's white-label config (`signup_button`); a reviewer may still override it.
   */
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected a #rrggbb colour').optional(),
  /**
   * The agency's white-label logo, inlined as a `data:` URI at generation (R4: nothing is fetched at
   * print time). Empty/absent renders no logo. Drawn in the page header when `showLogo` is on.
   */
  logo: z.string().optional(),
  /** Show the white-label logo in the page header. On by default; the reviewer can hide it. */
  showLogo: z.boolean().default(true),
  /** The Top Cities table (CTV/OTT). Shown unless turned off; the CTV builder enables it per report. */
  showCitiesChart: z.boolean().default(false),
  /*
   * The three targeting sections, each user-controllable and OFF by default. A section shows only
   * when its flag is explicitly true — set by the reviewer's checkbox or by a `*_targeting=Y` query
   * param on the report URL. Templates gate on `= true`, and the targeting PAGE itself hides when
   * all three are off so a report never carries a blank page.
   */
  showGeoTargeting: z.boolean().default(false),
  showAudienceTargeting: z.boolean().default(false),
  showTopSites: z.boolean().default(false),
  /*
   * Eblast click heatmap: overlay the creative with warm blobs over each clicked link, weighted by
   * clicks. Off by default. `heatmapDedicatedPage` decides overlay (on the link-detail creative) vs a
   * dedicated page per drop; ignored when the heatmap is off. Email reports only.
   */
  showClickHeatmap: z.boolean().default(false),
  heatmapDedicatedPage: z.boolean().default(false),
}).default({ showLogo: true, showCitiesChart: false, showGeoTargeting: false, showAudienceTargeting: false, showTopSites: false, showClickHeatmap: false, heatmapDedicatedPage: false });
export type Presentation = z.infer<typeof Presentation>;

/**
 * What the settings drawer may edit, and the control each field needs.
 *
 * ── Why a declared list rather than the rendered-path guard ──
 *
 * Editing a FIGURE is guarded by "does the document render this path", which is right for
 * a figure and cannot work for a setting: a `visibleWhen` flag is *used* by the template
 * without being *rendered* by it, so it would never appear in that set. A setting is
 * report configuration, and configuration is a closed set defined by the schema.
 *
 * The list lives beside the schema so a field cannot be added to `Presentation` and be
 * silently missing from the drawer, and so the API and the UI read the same declaration.
 *
 * ── What is deliberately absent ──
 *
 * The legacy drawer also carries SHOW NATIVE ICON, SHOW GEOFENCE ICON, INCLUDE SUMMARY
 * PAGE and COVER PAGE. None of those has anywhere to land: the block catalog has no
 * geofence or native marker, and no template defines a summary or cover page. Declaring
 * the fields anyway would put four switches in the drawer that do nothing, which is worse
 * than not having them — they need template work first, recorded in
 * `docs/25-report-builder.md`.
 *
 * Absent for a different reason: ADD CHANNEL(S), ADD EMAIL DROP(S), SHOW ALL ADGROUPS and
 * SHOW ALL CREATIVES. Those change what data is GATHERED, so they are inputs to
 * generation — the legacy Submit button re-runs the whole build — and cannot be an
 * override on an already-generated document.
 */
export const SETTINGS_FIELDS: Array<{
  path: string;
  label: string;
  kind: 'colour' | 'boolean' | 'text' | 'date' | 'integer';
  help?: string;
  /**
   * Channels this control applies to (uppercase, e.g. `['EMAIL']`). Omitted = every channel. The click
   * heatmap is an eblast-only feature — it renders a heatmap of email link clicks over an email drop's
   * creative — so its toggles must not appear on a display or audio report, where turning them on does
   * nothing and only misleads the reviewer.
   */
  channels?: string[];
}> = [
  /*
   * The reporting window.
   *
   * This is the ONE setting that can make a report lie, and it is included anyway because
   * the case it fixes is real: a report whose figures are right and whose stated period is
   * wrong. Editing the period does NOT re-aggregate — the figures came from the window the
   * report was generated for, and nothing here can change that.
   *
   * So the builder compares the stated period against `report.generatedAt`'s window and
   * says plainly when they have parted company. Making the field uneditable would leave a
   * mislabelled report unfixable; making it editable quietly would let someone relabel
   * January's delivery as February's. The third option is to allow it and refuse to be
   * silent about it.
   */
  { path: 'report.periodStart', label: 'Period start', kind: 'date',
    help: 'The window these figures describe. Changing it does NOT re-aggregate.' },
  { path: 'report.periodEnd', label: 'Period end', kind: 'date',
    help: 'Corrects a mislabelled period. To report a different window, regenerate.' },
  { path: 'campaign.clientName', label: 'Client name', kind: 'text',
    help: 'Printed in the page header and on the campaign card. Legacy: COMPANY NAME.' },
  { path: 'campaign.agencyName', label: 'Agency name', kind: 'text',
    help: 'The agency. Used as the footer copyright on every report.' },
  { path: 'campaign.notes', label: 'Report note', kind: 'text',
    help: 'A standing caveat printed under the campaign details.' },
  { path: 'presentation.themeColor', label: 'Theme color', kind: 'colour',
    help: 'The header background / primary brand color. White-label: the agency’s signup_tab_active.' },
  { path: 'presentation.accentColor', label: 'Accent color', kind: 'colour',
    help: 'The header underline and campaign ID# color. White-label: the agency’s signup_button.' },
  { path: 'presentation.showLogo', label: 'Show logo', kind: 'boolean',
    help: 'Show the agency white-label logo in the page header. Nothing shows when the agency has no logo.' },
  { path: 'presentation.showCitiesChart', label: 'Cities chart', kind: 'boolean', channels: ['VIDEO'],
    help: 'The Top Cities table on CTV/OTT reports.' },
  /*
   * CTV only: scale the whole report to a target impression total. Entering a number moves the headline,
   * video starts, top cities, apps, charts, completion funnel and impressions-by-day to it proportionally
   * (see scaleVideoToDesired). Clearing it returns to the measured figures.
   */
  { path: 'ctv.desiredImpressions', label: 'Desired Impressions', kind: 'integer', channels: ['VIDEO'],
    help: 'Scale the whole report to this impression total — top cities, apps, charts, funnel and daily all adjust proportionally to sum to it. Leave blank for the measured figures.' },
  /*
   * Audience / Top-Sites toggles are scoped to every channel EXCEPT email — an eblast has no demographic
   * or delivered-sites page — listed as an allowlist of the non-email channels, so EMAIL is excluded by
   * omission (the drawer shows a field only when its channels intersect the report's). Geographic Targeting
   * DOES apply to email: its optional geo page maps the cities recipients clicked from, so EMAIL is listed.
   */
  { path: 'presentation.showGeoTargeting', label: 'Geographic Targeting', kind: 'boolean',
    channels: ['DISPLAY', 'AUDIO', 'DOOH', 'VIDEO', 'EMAIL'],
    help: 'The geo targeting page with the map. On an eblast it maps the cities recipients clicked from.' },
  { path: 'presentation.showAudienceTargeting', label: 'Audience Targeting', kind: 'boolean',
    channels: ['DISPLAY', 'AUDIO', 'DOOH', 'VIDEO'],
    help: 'The demographic targeting section. Shows a note when the campaign targets broadly.' },
  { path: 'presentation.showTopSites', label: 'Top Sites', kind: 'boolean',
    channels: ['DISPLAY', 'AUDIO', 'DOOH', 'VIDEO'],
    help: 'The top delivering sites section (post-flight). Shows a note until site data exists.' },
  { path: 'presentation.showClickHeatmap', label: 'Click heatmap', kind: 'boolean', channels: ['EMAIL'],
    help: 'Eblast only: overlays the creative with a click heatmap of the links. Adds render time.' },
  { path: 'presentation.heatmapDedicatedPage', label: 'Heatmap on its own page', kind: 'boolean', channels: ['EMAIL'],
    help: 'When the click heatmap is on: put it on a dedicated page per drop instead of over the link-detail creative.' },
];

export const ReportDataModel = z.object({
  dataModelVersion: z.literal(DATA_MODEL_VERSION),
  metricsVersion: z.enum(['v1', 'v2']),
  lifecycle: z.enum(['PREFLIGHT', 'INTERIM', 'POSTFLIGHT']),

  /** Scope of the underlying record selection — see SelectionCriteria. */
  selection: SelectionCriteria,

  report: z.object({
    id: z.string(),
    version: z.number().int().positive(),
    generatedAt: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    /**
     * Set (ISO instant) when the window reaches TODAY, so the figures were consolidated on demand and are
     * still moving — the report renders an "as of <time>, provisional" note and re-settles as the day
     * closes. Absent on a fully-settled, past-window report.
     */
    provisionalAsOf: z.string().optional(),
  }),

  campaign: z.object({
    id: z.string(),
    name: z.string(),
    /** The reporting key. NOT `so` — see audit F10. */
    reportSo: z.string(),
    so: z.string().optional(),
    /** One reportSo may span several IOs; rare but real (45 of 16,258). */
    ios: z.array(z.string()).default([]),
    clientName: z.string(),
    agencyName: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    notes: z.string().optional(),
    bannerAssetId: z.string().optional(),
    channels: z.array(z.enum(['DISPLAY', 'EMAIL', 'AUDIO', 'VIDEO', 'DOOH'])),
  }),

  /** Campaign-wide rollup across every channel. */
  overall: z.object({
    delivery: DeliveryStats,
    contract: ContractStats,
    derived: DerivedStats,
    devices: z.array(NamedCount).default([]),
    browsers: z.array(NamedCount).default([]),
  }),

  channels: z.array(ChannelSummary).default([]),
  adGroups: z.array(AdGroup).default([]),
  targeting: Targeting,

  audio: AudioStats.optional(),
  video: AudioStats.optional(),
  /** CTV / OTT connected-TV delivery — see CtvStats. Distinct from `video` (audio-shaped legacy slot). */
  ctv: CtvStats.optional(),
  email: EmailStats.optional(),
  dooh: DoohStats.optional(),

  /** How this report is drawn. See Presentation — settings, not measurements. */
  presentation: Presentation,

  /**
   * Audit surface (scope R2/R6). Lets a template render a provenance note or an
   * "adjusted figures" marker without reaching outside the data model.
   */
  provenance: z.object({
    overrideCount: Int.default(0),
    overridesByKind: z.object({
      DATA_GAP: Int.default(0),
      CONTRACTUAL: Int.default(0),
      PRESENTATION: Int.default(0),
    }).default({ DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 }),
    hasAdjustedFigures: z.boolean().default(false),
  }),
});
export type ReportDataModel = z.infer<typeof ReportDataModel>;
